import React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ColumnDef } from '@tanstack/react-table';
import { AxiosError } from 'axios';
import { useIntl } from 'react-intl';

import ViewAllButton from 'src/components/ViewAllButton';
import { PERMISSION } from 'src/features/auth';
import { EVENT_TYPE } from 'src/features/eventLog/types';
import { eventTypeBadgeOptions } from 'src/features/eventLog/utils/constants';
import CellEventIdentifier from 'src/features/home/components/CellEventIdentifier';
import { StatisticEvent } from 'src/features/home/types';
import { homeMessages } from 'src/features/home/utils/messages';
import { PATH } from 'src/features/navigation/utils/constants';
import { SEARCH_STAY_RESOURCE, searchStay } from 'src/features/stay/api/useSearchStay';
import ActiveStaysSidePanel from 'src/features/stay/components/sidePanel/ActiveStaysSidePanel';
import PastStaysSidePanel from 'src/features/stay/components/sidePanel/PastStaysSidePanel';
import useHasPermissions from 'src/hooks/useHasPermissions';
import { toast } from 'src/hooks/UseToast';
import { queryClient } from 'src/lib/react-query';
import { useParking } from 'src/providers/ParkingProvider';
import { GeneralBlock } from 'src/registry/components/blocks/GeneralBlock';
import GeneralMessageBlock from 'src/registry/components/blocks/GeneralMessageBlock';
import SidePanel from 'src/registry/components/sidePanel/SidePanel';
import StatisticTable from 'src/registry/components/statistic/StatisticTable';
import { useSidePanel } from 'src/registry/providers/SidePanelProvider';
import { Badge } from 'src/registry/ui/Badge';
import { generalErrorMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { cn } from 'src/utils/shadcn';

type Props = {
    data?: StatisticEvent[];
    totalEvents?: number;
    isError?: boolean;
};

const WIDGET_EVENTS_ACTIVE_STAY_PANEL = 'widget-events-active-stay-panel';
const WIDGET_EVENTS_PAST_STAY_PANEL = 'widget-events-past-stay-panel';

export default function WidgetEvents({ data, totalEvents, isError }: Props) {
    const { formatMessage, locale } = useIntl();
    const { parkingId } = useParking();
    const permissionAccessEventLogs = useHasPermissions([PERMISSION.ACCESS_EVENT_LOGS]);

    const { openPanel, closePanel } = useSidePanel();
    const [selectedStay, setSelectedStay] = React.useState<{
        id: string;
        isActive: boolean;
        eventType?: EVENT_TYPE;
    } | null>(null);

    const handleIdentifierClick = async (identifier: string, eventType?: EVENT_TYPE) => {
        try {
            const stay = await queryClient.fetchQuery({
                queryKey: [SEARCH_STAY_RESOURCE, identifier],
                queryFn: () => searchStay({ parkingId, license_plate: identifier }),
            });
            const isActive = stay.exit_time === null;

            setSelectedStay({ id: stay.id, isActive, eventType });
            openPanel(isActive ? WIDGET_EVENTS_ACTIVE_STAY_PANEL : WIDGET_EVENTS_PAST_STAY_PANEL);
            closePanel(isActive ? WIDGET_EVENTS_PAST_STAY_PANEL : WIDGET_EVENTS_ACTIVE_STAY_PANEL);
        } catch (error) {
            // Show the backend's message for client errors (4xx, e.g. "stay not found"); for server
            // errors (5xx) or no response keep the generic message
            const response = (error as AxiosError<string>).response;
            const description =
                response && response.status < 500 && typeof response.data === 'string' && response.data
                    ? response.data
                    : formatMessage(generalErrorMessages.unknownError);
            toast({
                title: formatMessage(generalErrorMessages.somethingWentWrong),
                description,
                variant: 'destructive',
            });
        }
    };

    const columns: ColumnDef<StatisticEvent>[] = [
        {
            accessorKey: 'timestamp',
            header: formatMessage(homeMessages.time),
            cell: (info) =>
                new Date(info.getValue() as string).toLocaleTimeString(locale, {
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit',
                    hour12: false,
                }),
            maxSize: 60,
        },
        {
            accessorKey: 'event_type',
            header: formatMessage(homeMessages.type),
            cell: (info) => {
                const rawType = info.getValue() as EVENT_TYPE;
                const option = eventTypeBadgeOptions[rawType];
                const { iconColor } = option?.color ?? { iconColor: undefined };

                return (
                    <Badge
                        theme={option?.theme || 'gray'}
                        className="flex w-full min-w-0 items-center justify-start gap-1"
                    >
                        {option?.icon && (
                            <FontAwesomeIcon
                                icon={option.icon}
                                className={cn('z-0', !iconColor && !option.theme && 'opacity-50')}
                                style={{ color: iconColor }}
                                size={option.iconSize}
                            />
                        )}
                        <span className="min-w-0 truncate">{option ? formatMessage(option.label) : rawType}</span>
                    </Badge>
                );
            },
        },
        {
            accessorKey: 'event_data',
            header: formatMessage(homeMessages.identifier),
            cell: ({ row }) => (
                <CellEventIdentifier
                    eventData={row.original.event_data}
                    event={row.original}
                    handleIdentifierClick={handleIdentifierClick}
                />
            ),
        },
    ];

    return (
        <GeneralBlock className="min-w-0 flex-1 gap-6">
            <div className="flex items-start justify-between">
                <div className="flex gap-6">
                    <div className="flex size-14 items-center justify-center rounded-full border-8 border-green-50 bg-green-100 p-1 text-2xl text-green-700">
                        <FontAwesomeIcon icon={ICON_MAP.TREND_UP} />
                    </div>
                    <div className="flex flex-col">
                        <span className="text-sm font-semibold text-gray-600">
                            {formatMessage(homeMessages.totalEventsToday)}
                        </span>
                        <span className="text-lg font-bold">{isError ? '-' : totalEvents}</span>
                    </div>
                </div>
                {permissionAccessEventLogs && <ViewAllButton route={`/${PATH.VISITORS}/event-log`} />}
            </div>
            {isError ? (
                <GeneralMessageBlock variant="error" />
            ) : (
                // Keep the table within the widget: when a side panel opens the grid track shrinks and the
                // full-width table no longer fits, so allow horizontal scrolling instead of letting the
                // identifier value spill outside the container.
                <div className="w-full overflow-x-auto">
                    <StatisticTable
                        className="w-full"
                        title={formatMessage(homeMessages.lastTenEvents)}
                        data={data}
                        columns={columns as any}
                    />
                    <SidePanel onClose={() => setSelectedStay(null)} name={WIDGET_EVENTS_PAST_STAY_PANEL}>
                        <PastStaysSidePanel selectedStayId={selectedStay?.id} eventType={selectedStay?.eventType} />
                    </SidePanel>
                    <SidePanel onClose={() => setSelectedStay(null)} name={WIDGET_EVENTS_ACTIVE_STAY_PANEL}>
                        <ActiveStaysSidePanel selectedStayId={selectedStay?.id} eventType={selectedStay?.eventType} />
                    </SidePanel>
                </div>
            )}
        </GeneralBlock>
    );
}
